import { SeoLandingLayout, type SeoPageContent } from '@/components/seo/SeoLandingLayout'

const content: SeoPageContent = {
  locale: 'de',
  path: '/co-parenting-ausgaben-teilen',
  alternatePath: '/co-parenting-expense-sharing',
  seo: {
    title: 'Co-Parenting — Kinderkosten fair teilen mit Equna',
    description:
      'Equna hilft getrennt lebenden Eltern, Kinderkosten transparent zu erfassen, Belege zu speichern und nach einer vereinbarten Regel aufzuteilen — ohne Streit, ohne Excel.',
  },
  hero: {
    eyebrow: 'Für Co-Parents',
    h1: 'Kinderkosten fair teilen — auch wenn ihr getrennt lebt',
    sub: 'Schule, Kleidung, Hobbys, Arzt: Equna sammelt alles an einem Ort, dokumentiert Belege und teilt nach eurer Regel auf. Keine Diskussionen, kein Bauchgefühl — nur Zahlen, die beide Eltern sehen.',
    note: 'Kostenlos starten. Eure Daten bleiben in Deutschland.',
  },
  problems: {
    heading: 'Warum Kinderkosten so schnell zur Belastung werden',
    intro:
      'Nach einer Trennung gibt es viele neue Themen — und Geld ist eines der schwierigsten. Genau dann fehlt der gemeinsame Ort, an dem Ausgaben sauber dokumentiert sind.',
    items: [
      {
        title: 'Belege liegen überall',
        description:
          'Kassenzettel vom Schulbedarf, E-Mail vom Sportverein, WhatsApp-Foto vom Arztrezept — alles an unterschiedlichen Stellen.',
      },
      {
        title: 'Wer hat wann was übernommen?',
        description:
          'Ohne klare Dokumentation entstehen unterschiedliche Erinnerungen — und damit Streit über Kleinigkeiten, der schnell groß wird.',
      },
      {
        title: 'Unterschiedliche Aufteilung je Kategorie',
        description:
          'Kleidung 50/50, Schule anteilig, Hobbys nach Einkommen — solche Regeln im Kopf zu halten funktioniert nicht.',
      },
      {
        title: 'Kein klarer Abschluss',
        description:
          'Ohne Monatsschnitt zieht sich der Rückstand mit. Eltern haben Gefühl-Schulden, ohne dass jemand die Zahlen sieht.',
      },
    ],
  },
  solutions: {
    heading: 'Wie Equna Co-Parents konkret entlastet',
    intro:
      'Eine App, beide Eltern, dieselbe Sicht. Ausgaben werden eingetragen, sobald sie passieren — und am Monatsende ist der Saldo klar.',
    items: [
      {
        icon: 'kids',
        title: 'Ausgaben pro Kind',
        description:
          'Mehrere Kinder? Trag jede Ausgabe direkt dem richtigen Kind zu — saubere Auswertung pro Kind möglich.',
      },
      {
        icon: 'receipt',
        title: 'Beleg direkt mit dabei',
        description:
          'Foto vom Kassenzettel, Screenshot der E-Rechnung. Dokumentation passiert in dem Moment, wenn das Geld fließt.',
      },
      {
        icon: 'split',
        title: 'Eure Verteilungsregel',
        description:
          '50/50, anteilig nach Einkommen oder individuelle Aufteilung. Einmal vereinbart, immer angewendet.',
      },
      {
        icon: 'calendar',
        title: 'Monatsabschluss als Schnitt',
        description:
          'Beide Eltern bestätigen den Monat. Equna berechnet, wer wem wie viel überweist. Klare Linie, dann wieder bei Null.',
      },
      {
        icon: 'shield',
        title: 'Sachlich, nicht emotional',
        description:
          'Statt Diskussionen in WhatsApp habt ihr Zahlen vor euch. Beide Eltern sehen denselben Stand und dieselbe Historie.',
      },
      {
        icon: 'check',
        title: 'Auch für Streitfälle dokumentiert',
        description:
          'Sollte es einmal Unstimmigkeiten geben, sind alle Beträge mit Datum und Beleg nachvollziehbar — ohne Mailsuche.',
      },
    ],
  },
  comparison: {
    heading: 'Vorher vs. mit Equna',
    columns: ['Ohne App', 'Mit Equna'],
    rows: [
      {
        topic: 'Kinderausgaben erfassen',
        without: 'Zettel, WhatsApp, Excel-Datei',
        with: '10-Sekunden-Eintrag, beide Eltern sehen es',
      },
      {
        topic: 'Belege',
        without: 'Zerstreut auf Mail, Foto-Galerie, Tasche',
        with: 'Direkt an der Ausgabe gespeichert',
      },
      {
        topic: 'Aufteilung',
        without: 'Diskutiert pro Posten',
        with: 'Einmal definiert, automatisch angewendet',
      },
      {
        topic: 'Mehrere Kinder',
        without: 'Manuelles Filtern in Listen',
        with: 'Direkte Zuordnung + Auswertung pro Kind',
      },
      {
        topic: 'Monatsschnitt',
        without: 'Findet meistens nicht statt',
        with: 'Beide bestätigen, App rechnet aus',
      },
    ],
  },
  trust: {
    heading: 'Warum Equna für Co-Parents passt',
    items: [
      {
        title: 'Made in Germany',
        description: 'Server in Deutschland, Betreiber in Berlin. Eure Daten verlassen die EU nicht.',
      },
      {
        title: 'DSGVO-konform',
        description: 'Belege und Ausgaben bleiben bei euch — keine Weitergabe an Werbenetzwerke.',
      },
      {
        title: 'Beide Eltern gleichberechtigt',
        description: 'Keine Hauptperson, kein Anhang. Beide Konten haben dieselben Rechte.',
      },
      {
        title: 'Kostenlos starten',
        description: 'Die Grundversion deckt alle Co-Parenting-Funktionen ab — ohne Zeitlimit.',
      },
    ],
  },
  relatedBlog: {
    heading: 'Mehr zum Thema lesen',
    posts: [
      {
        title: 'Haushaltskosten fair teilen — 5 Modelle',
        description: 'Welches Aufteilungsmodell passt zu eurer Konstellation? Vier Beispiele plus Empfehlung.',
        href: '/blog/haushaltskosten-fair-teilen-fuenf-modelle',
      },
      {
        title: 'Equna vs. Splitwise',
        description: 'Warum Splitwise für einmalige Beträge stark ist und Equna für laufende Co-Parenting-Kosten gemacht ist.',
        href: '/blog/equna-vs-splitwise-fuer-paare-und-familien',
      },
      {
        title: '3-Konten-Modell und Equna',
        description: 'Wie ein gemeinsames Konto für Kinderkosten zusammen mit Equna funktioniert.',
        href: '/blog/3-konten-modell-und-equna-warum-beides-zusammengehoert',
      },
    ],
  },
  faq: {
    heading: 'Häufige Fragen von Co-Parents',
    items: [
      {
        question: 'Müssen beide Elternteile Equna nutzen?',
        answer:
          'Sinnvoll ist es, wenn beide die App haben — dann sehen beide dieselben Zahlen in Echtzeit. Du kannst aber auch starten und den anderen Elternteil später einladen.',
      },
      {
        question: 'Wie funktioniert das mit mehreren Kindern?',
        answer:
          'Du legst die Kinder einmal an und ordnest jede Ausgabe einem Kind zu. So bekommst du saubere Auswertungen pro Kind — etwa für Steuer, Unterhalt oder Diskussionen mit dem anderen Elternteil.',
      },
      {
        question: 'Können wir verschiedene Aufteilungsregeln pro Bereich?',
        answer:
          'Es gibt eine globale Regel, die für alle Ausgaben gilt. Bei einzelnen Ausgaben kannst du eine abweichende Aufteilung wählen — etwa wenn nur ein Elternteil eine bestimmte Anschaffung übernimmt.',
      },
      {
        question: 'Kann ich die Daten exportieren — z.B. für die Anwältin?',
        answer:
          'Ja. Du kannst deine Daten als DSGVO-Export herunterladen. Im Streitfall gibt es damit eine nachvollziehbare Historie mit Datum, Betrag und Beleg.',
      },
      {
        question: 'Ist Equna kostenlos?',
        answer:
          'Ja. Co-Parenting-Funktionen wie Kind-Ausgaben, Belege (60 Tage) und Monatsabschluss sind kostenlos. Eine Premium-Variante mit längerer Beleg-Speicherung ist optional.',
      },
    ],
  },
  finalCta: {
    heading: 'Klare Kinderkosten — ohne Streit um Kleinigkeiten',
    sub: 'Equna installieren, das andere Elternteil einladen, Verteilung festlegen.',
  },
  uiStrings: {
    backToHome: '← Zur Startseite',
    related: 'Mehr lesen',
    legalPrivacy: 'Datenschutz',
    legalTerms: 'AGB',
    legalImprint: 'Impressum',
    legalSupport: 'Support',
    copyrightRights: 'Alle Rechte vorbehalten.',
    breadcrumbHome: 'Startseite',
    breadcrumbCurrent: 'Co-Parenting Ausgaben teilen',
  },
}

export default function CoParentingSeoPage() {
  return <SeoLandingLayout content={content} />
}
